import React, { useEffect, useState, useCallback, useRef } from "react";
import { HelpCircle, X, ArrowRight, ArrowLeft } from "lucide-react";

const TOUR_KEY = "anx_tour_v1";

const STEPS = [
  { nav: "tab:switchboard", target: "rail-nav-switchboard", title: "Your Main Menu", body: "Everything starts here — the same Switchboard layout as your Spa Buddy database. Recognise, don't recall." },
  { nav: "tab:search", target: "rail-nav-search", title: "Find Anyone Fast", body: "Type a name, phone number, address or J-number to jump straight to any client in seconds." },
  { nav: "tab:clients", target: "rail-nav-clients", title: "Open a Client", body: "Your full client list. Click a client tile to open their card — then use the Invoices, Quotes and Work Orders tabs to see all 15 years of their history." },
  { nav: "tab:pos", target: "rail-nav-pos", title: "Point of Sale", body: "Ring up a counter sale on the big touch grid, exactly like the old POS screen." },
  { nav: "tab:reports", target: "rail-nav-reports", title: "Reports", body: "Debtors & outstanding, Spa Sales History and live Stock Levels — one click each." },
  { nav: "tab:colours", target: "rail-nav-colours", title: "Colour Swatches", body: "Every shell & cabinet colour with its real photo, ready to show customers when quoting." },
  { nav: "tab:switchboard", target: null, title: "You're all set!", body: "That's the whole layout. Tap the blue ? button in the bottom-right corner any time to run this tour again." },
];

export default function GuidedTour({ onNav }) {
  const [open, setOpen] = useState(false);
  const [i, setI] = useState(0);
  const [rect, setRect] = useState(null);
  const timer = useRef(null);

  const step = STEPS[i];
  const navRef = useRef(onNav);
  useEffect(() => { navRef.current = onNav; });

  const measure = useCallback(() => {
    if (!step || !step.target) { setRect(null); return; }
    const el = document.querySelector(`[data-testid="${step.target}"]`);
    if (el) {
      const r = el.getBoundingClientRect();
      setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    } else { setRect(null); }
  }, [step]);

  // navigate + measure only when the tour opens or the step index changes
  useEffect(() => {
    if (!open) return;
    const st = STEPS[i];
    if (st?.nav) navRef.current?.(st.nav);
    clearTimeout(timer.current);
    timer.current = setTimeout(measure, 320);
    return () => clearTimeout(timer.current);
  }, [open, i]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open) return;
    const h = () => measure();
    window.addEventListener("resize", h);
    window.addEventListener("scroll", h, true);
    return () => { window.removeEventListener("resize", h); window.removeEventListener("scroll", h, true); };
  }, [open, measure]);

  useEffect(() => {
    if (!localStorage.getItem(TOUR_KEY)) {
      const t = setTimeout(() => { setI(0); setOpen(true); }, 900);
      return () => clearTimeout(t);
    }
  }, []);

  const finish = () => { localStorage.setItem(TOUR_KEY, "1"); setOpen(false); setI(0); };
  const start = () => { setI(0); setOpen(true); };
  const next = () => (i < STEPS.length - 1 ? setI(i + 1) : finish());
  const back = () => setI(Math.max(0, i - 1));

  // tooltip position: beside the highlighted element, else centre
  let cardStyle = { top: "50%", left: "50%", transform: "translate(-50%,-50%)" };
  if (rect) {
    const vw = window.innerWidth;
    const preferRight = rect.left + rect.width + 360 < vw;
    const top = Math.min(Math.max(rect.top - 8, 12), window.innerHeight - 240);
    cardStyle = preferRight
      ? { top, left: rect.left + rect.width + 16 }
      : { top, left: Math.max(12, rect.left - 360) };
  }

  return (
    <>
      <button onClick={open ? finish : start} data-testid="guided-tour-btn"
        className="fixed bottom-5 right-5 z-[60] h-12 w-12 rounded-full bg-[#123a63] text-white shadow-lg flex items-center justify-center hover:bg-[#1a4f8a] transition-colors"
        title="Guided tour" aria-label="Guided tour">
        <HelpCircle className="h-6 w-6" />
      </button>

      {open && (
        <div className="fixed inset-0 z-[59]" data-testid="guided-tour-overlay">
          <div className="absolute inset-0 bg-slate-900/55" onClick={finish} />
          {rect && (
            <div className="absolute rounded-xl ring-4 ring-sky-400 pointer-events-none transition-all duration-300"
              style={{ top: rect.top - 4, left: rect.left - 4, width: rect.width + 8, height: rect.height + 8, boxShadow: "0 0 0 9999px rgba(15,23,42,0.55)" }} />
          )}
          <div className="absolute w-[340px] max-w-[90vw] bg-white rounded-2xl shadow-2xl p-5 transition-all duration-300" style={cardStyle} data-testid="guided-tour-card">
            <button onClick={finish} data-testid="guided-tour-skip" className="absolute top-3 right-3 text-slate-400 hover:text-slate-700"><X className="h-4 w-4" /></button>
            <p className="text-[11px] font-bold uppercase tracking-wider text-sky-600 mb-1">Step {i + 1} of {STEPS.length}</p>
            <h3 className="font-display text-lg font-bold text-slate-900 mb-1.5">{step.title}</h3>
            <p className="text-sm text-slate-600 leading-relaxed mb-4">{step.body}</p>
            <div className="flex items-center justify-between">
              <button onClick={back} disabled={i === 0} data-testid="guided-tour-back"
                className={`inline-flex items-center gap-1 text-sm font-semibold ${i === 0 ? "text-slate-300 cursor-default" : "text-slate-500 hover:text-slate-800"}`}>
                <ArrowLeft className="h-4 w-4" /> Back
              </button>
              <div className="flex items-center gap-1.5">
                {STEPS.map((_, k) => <span key={k} className={`h-1.5 rounded-full transition-all ${k === i ? "w-5 bg-sky-500" : "w-1.5 bg-slate-200"}`} />)}
              </div>
              <button onClick={next} data-testid="guided-tour-next"
                className="inline-flex items-center gap-1 rounded-full bg-[#123a63] text-white text-sm font-bold px-4 py-2 hover:bg-[#1a4f8a]">
                {i === STEPS.length - 1 ? "Done" : "Next"}{i < STEPS.length - 1 && <ArrowRight className="h-4 w-4" />}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
